
import axios from 'axios';
import { notifySessionUnauthorized } from "../auth/sessionUnauthorizedEvents";
import { authedFetch } from "../auth/authedFetch";
import { getApiBaseUrl } from "../utils/urlHandler";
import { MediaItem } from '../types/MediaItem';
import { createPostsFromChat, updatePostFields, type PostCarouselMediaItem } from './postService';
import { fetchCustomerById } from './customerService';
import { mapAllowedPlatforms } from '../utils/allowedPlatformMapping';
import { debug } from "../utils/debugLogger";
import {
  Avatar,
  Voice,
  GenerateUGCVideoParams,
  VideoGenerationResponse,
  VideoStatusResponse,
  UGCVideoDraft,
  CaptionCue,
} from '../features/ugc-video/types';
// Interface for presigned URL request
export interface PresignedUrlRequest {
  customer_id: string;
  file_name: string;
  user_email: string;
  // Flags for what is being uploaded
  is_image?: boolean;
  is_video?: boolean;
  is_document?: boolean;
  is_training_data?: boolean;
  // Content types for each type
  image_content_type?: string;
  video_content_type?: string;
  document_content_type?: string;
  training_data_content_type?: string;
  // Optional fields for legacy or extra info
  image_name?: string;
  video_name?: string;
  image_type?: string;
  video_type?: string;
  logo?: boolean;
}

export interface MediaRegistration {
  customer_id: string;
  media_type: "IMAGE" | "VIDEO" | "DOCUMENT";
  s3_key: string;
  cloudfront_url: string;
  metadata: {
    size: number;
    dimensions?: string;  // For images
    duration?: number;    // For videos
    content_type: string;
  };
  created_by: string;
  tags?: string[];
  processed: boolean;
}

export interface MediaRegistrationResponse {
  media_id?: string;
  id?: string;
  message?: string;
  [key: string]: any;
}


// Single source: REACT_APP_CONTENT_API_URL = full API base (e.g. https://xxx/Prod/content). Paths are appended with no extra prefix.
const BASE_URL = getApiBaseUrl("REACT_APP_CONTENT_API_URL");
debug.log(`Using Content API URL (${process.env.REACT_APP_ENV}):`, BASE_URL);
const PRESIGNED_URL_ENDPOINT = `${BASE_URL}/s3-upload-url`;

// Helper to build the post generator endpoint in the format /content/{customer_id}/content-generate
const getPostGeneratorEndpoint = (customerId: string) => {
  return `${BASE_URL}/${customerId}/content-generate`;
};
  

export interface S3PresignedUrlResponse {
  image?: {
    uploadURL: string;
    fileInfo: {
      bucket: string;
      key: string;
      filename: string;
    };
    cloudFrontURL?: string;
  };
  video?: {
    uploadURL: string;
    fileInfo: {
      bucket: string;
      key: string;
      filename: string;
    };
    cloudFrontURL?: string;
  };
  trainingData?: {
    uploadURL: string;
    fileInfo: {
      bucket: string;
      key: string;
      filename: string;
      /** Signed Content-Type. Must be sent verbatim as the PUT header or S3 returns 403. */
      contentType?: string;
    };
  };
}

interface PostAssistantResponse {
  message: string;
  html_url: string;
  platforms?: Array<{
    platform: string;
    business_profile_id: string;
    post_id: string;
  }>;
  cloudfront_url?: string;
}

const myHeaders = new Headers();
myHeaders.append("Content-Type", "image/jpeg");

// Generate presigned URL
export const generatePresignedUrl = async (
  req: PresignedUrlRequest,
  idToken: string
) => {
  const response = await axios.post(PRESIGNED_URL_ENDPOINT, req, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });
  debug.log("Presigned URL response:", response.data);
  return response.data;
};

// Upload file to S3 using presigned URL
export const uploadFileToS3 = async (
  presignedUrl: string,
  file: File,
  signedContentType?: string
): Promise<void> => {
  // S3 rejects the PUT unless Content-Type matches what was signed, so the order below
  // is a fallback chain from most to least authoritative:
  //
  // 1. signedContentType — passed by callers whose backend derived the type itself
  //    rather than echoing File.type (knowledge files, where the browser's MIME is
  //    unreliable: Windows reports .csv as application/vnd.ms-excel and .md as "").
  // 2. the URL's content-type query param — only present under SigV2. Under SigV4,
  //    botocore's default, the type is a signed HEADER and never appears in the query,
  //    so this lookup returns null and is kept only for legacy/pre-signed-elsewhere URLs.
  // 3. File.type — correct for the image/video paths, where the backend signs exactly
  //    the MIME the browser sent it.
  const url = new URL(presignedUrl);
  const contentTypeFromUrl = url.searchParams.get('content-type');

  const contentType = signedContentType || contentTypeFromUrl || file.type;

  const headers = new Headers();
  if (contentType) {
    headers.append('Content-Type', contentType);
  }

  const requestOptions: RequestInit = {
    method: 'PUT',
    headers: headers,
    body: file,
  };

  const uploadResponse = await fetch(presignedUrl, requestOptions);

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text().catch(() => 'Unknown error');
    throw new Error(`Failed to upload file. Status: ${uploadResponse.status}, Error: ${errorText}`);
  }

  debug.log('File uploaded successfully');
};

export const generatePostByMedia = async (
  cloudFrontImageUrl: string,
  cloudFrontVideoUrl: string,
  assistant_id: string,
  userNote: string,
  customerId: string,
  scheduledDateTime: string,
  idToken: string,
  allowedPlatforms?: Array<{ platform: string; business_profile_id: string }>
): Promise<PostAssistantResponse> => {
  // Log the incoming CloudFront URLs
  debug.log("[generatePostByMedia] Received CloudFront URLs:", {
    imageUrl: cloudFrontImageUrl,
    videoUrl: cloudFrontVideoUrl
  });

  const payload: any = {
    image_cloudfront_url: cloudFrontImageUrl,
    video_cloudfront_url: cloudFrontVideoUrl,
    assistant_id: assistant_id,
    user_input: userNote,
    customer_id: customerId,
    scheduled_time: scheduledDateTime
  };
  
  // Log the payload that will be sent to the backend
  debug.log("[generatePostByMedia] Payload:", payload);
  
  if (allowedPlatforms && allowedPlatforms.length > 0) {
    payload.allowed_platforms = allowedPlatforms;
  }

  const endpoint = getPostGeneratorEndpoint(customerId);
  const response = await axios.post(
    endpoint,
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
    }
  );

  if (response.status !== 200) {
    throw new Error(`Failed to send CloudFront URLs. Status: ${response.status}`);
  }

  debug.log('[generatePostByMedia] CloudFront URLs successfully sent to backend');
  debug.log('[generatePostByMedia] Backend response:', response.data);
  
  // Return all relevant response data, including platforms and cloudfront_url if present
  return {
    message: response.data.message,
    html_url: response.data.html_url,
    platforms: response.data.platforms,
    cloudfront_url: response.data.cloudfront_url
  };
};


/** Map API rows to MediaItem; fills `cloudfront_url` / `media_type` when the backend uses alternate keys. */
function normalizeCustomerMediaItem(raw: Record<string, unknown>): MediaItem {
  const s3Key = String(raw.s3_key ?? '');
  const cloudfront_url =
    String(raw.cloudfront_url ?? '').trim() ||
    String(raw.url ?? '').trim() ||
    String(raw.signed_url ?? '').trim() ||
    String(raw.thumbnail_url ?? '').trim() ||
    String(raw.cdn_url ?? '').trim() ||
    String(raw.public_url ?? '').trim();
  let media_type = String(raw.media_type ?? '').trim();
  if (!media_type) {
    const key = s3Key.toLowerCase();
    if (/\.(mp4|webm|mov|m4v|mkv)(\?|#|$)/i.test(key)) media_type = 'video';
    else if (/\.pdf(\?|#|$)/i.test(key)) media_type = 'document';
    else if (/\.(jpe?g|png|gif|webp|avif|bmp)(\?|#|$)/i.test(key)) media_type = 'image';
    else media_type = 'image';
  }
  const meta = raw.metadata as MediaItem['metadata'] | undefined;
  return {
    processed: Boolean(raw.processed),
    metadata:
      meta && typeof meta.size === 'number'
        ? meta
        : { size: 0, dimensions: typeof meta?.dimensions === 'string' ? meta.dimensions : '0x0' },
    media_type,
    s3_key: s3Key,
    media_id: String(raw.media_id ?? ''),
    created_at: String(raw.created_at ?? ''),
    created_by: raw.created_by != null ? String(raw.created_by) : undefined,
    cloudfront_url,
    tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : [],
  };
}

export interface FetchMediaOptions {
  limit?: number;
  nextToken?: string | null;
  mediaType?: "image" | "video" | "document";
  signal?: AbortSignal;
}

export interface FetchMediaResult {
  medias: MediaItem[];
  nextToken: string | null;
}

export const fetchMediaByCustomer = async (
  customerId: string,
  idToken: string,
  opts: FetchMediaOptions = {}
): Promise<FetchMediaResult> => {
  const { limit, nextToken, mediaType, signal } = opts;
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", String(limit));
  if (nextToken) params.set("next_token", nextToken);
  if (mediaType) params.set("media_type", mediaType);

  const query = params.toString();
  const apiUrl = `${BASE_URL}/media/customers/${customerId}${query ? `?${query}` : ""}`;
  const response = await authedFetch(
    apiUrl,
    {
      signal,
      headers: {
        "Content-Type": "application/json",
      },
    },
    idToken
  );
  if (!response.ok) {
    notifySessionUnauthorized(response.status);
    let detail = `Request failed (${response.status})`;
    try {
      const errBody = await response.json();
      if (typeof errBody?.message === "string") detail = errBody.message;
      else if (typeof errBody?.error === "string") detail = errBody.error;
    } catch {
      /* ignore non-JSON error bodies */
    }
    throw new Error(detail);
  }
  const data: unknown = await response.json();
  const rawList: Record<string, unknown>[] = Array.isArray(data)
    ? (data as Record<string, unknown>[])
    : (((data as { medias?: Record<string, unknown>[]; media?: Record<string, unknown>[] })?.medias ??
        (data as { medias?: Record<string, unknown>[]; media?: Record<string, unknown>[] })?.media) ??
      []);
  const medias: MediaItem[] = rawList.map((row) => normalizeCustomerMediaItem(row));
  const cursor =
    (data as { next_token?: string | null })?.next_token ?? null;
  return { medias, nextToken: cursor };
};


export interface ImportedMedia {
  media_id: string;
  cloudfront_url: string;
  s3_key: string;
  content_type: string;
  size: number;
}

export interface ImportedMediaResult extends ImportedMedia {
  /** Only when `darkVariant: "generate"` was asked (SGL-547); null/absent when none. */
  dark?: (ImportedMedia & { how?: "copy-opaque" | "copy-light" | "white-ink" }) | null;
}

/**
 * Copy an image from a public web address into our own storage (SGL-512): a
 * logo found on the owner's website. The browser can't download it itself (no
 * CORS), and a third-party URL must never be saved on the Brand.
 */
export const importMediaFromUrl = async (
  customerId: string,
  sourceUrl: string,
  idToken: string,
  tag = "logo",
  options: { darkVariant?: "generate" } = {}
): Promise<ImportedMediaResult> => {
  try {
    // A plain instance, without the app's global interceptors: this copy is
    // optional (the owner can upload instead), so its 403 must not raise the
    // "Sign in required" dialog. The caller passes a fresh token.
    const response = await axios.create().post(
      `${BASE_URL}/media/import-url`,
      {
        customer_id: customerId,
        source_url: sourceUrl,
        tag,
        ...(options.darkVariant ? { dark_variant: options.darkVariant } : {}),
      },
      { headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` } }
    );
    return response.data as ImportedMediaResult;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { error?: string } | undefined;
      throw new Error((typeof data?.error === "string" && data.error) || error.message);
    }
    throw error;
  }
};

export const registerMediaWithBackend = async (
  media: MediaRegistration,
  idToken: string
): Promise<MediaRegistrationResponse> => {
  const endpoint = `${BASE_URL}/media`;
  
  try {
    const response = await axios.post(
      endpoint,
      media,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
      }
    );
    
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to register media. Status: ${response.status}`);
    }
    
    debug.log('Media registered successfully:', response.data);
    return response.data;
  } catch (error) {
    debug.error('Error registering media:', error);
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { message?: string; error?: string } | undefined;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        error.message;
      throw new Error(msg || 'Failed to register media with backend');
    }
    throw new Error('Failed to register media with backend');
  }
};

/**
 * Deletes a media item from the backend.
 * @param mediaId The ID of the media item to delete.
 * @param customerId The customer ID associated with the media.
 * @param idToken The Cognito ID token for authorization.
 * @returns Promise that resolves when media is deleted successfully.
 */
export const deleteMedia = async (
  mediaId: string,
  customerId: string,
  idToken: string
): Promise<void> => {
  const endpoint = `${BASE_URL}/media/${mediaId}?customer_id=${customerId}`;
  
  try {
    const response = await axios.delete(endpoint, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      },
    });
    
    if (response.status !== 200) {
      throw new Error(`Failed to delete media. Status: ${response.status}`);
    }
    
    debug.log('Media deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    debug.error('Error deleting media:', error);
    throw new Error('Failed to delete media from backend');
  }
};

// Generate Image interfaces
export type AspectRatio = "1x1" | "16x9" | "9x16" | "4x3" | "3x4";
export type ImageProvider = "openai" | "gemini";
export type OpenAIQuality = "low" | "medium" | "high" | "auto";

export interface GenerateImageRequest {
  prompt: string;
  customer_id: string;
  provider: ImageProvider;
  num_images: number;
  aspect_ratio: AspectRatio;
  // OpenAI-only; ignored by other providers
  quality?: OpenAIQuality;
}

export interface GeneratedImage {
  image_url: string;
  image_index: number;
  media_id: string;
  s3_key: string;
  metadata: {
    source: string;
    prompt: string;
    aspect_ratio: string;
    image_index: number;
    generation_batch: number;
    rendering_speed?: string; // Optional for backward compatibility
  };
}

export interface GenerateImageResponse {
  success: boolean;
  images: GeneratedImage[];
  error?: string;
}

/**
 * Generate images using AI
 * @param request The generate image request parameters
 * @param idToken The Cognito ID token for authorization
 * @returns Promise<GenerateImageResponse>
 */
export const generateImage = async (
  request: GenerateImageRequest,
  idToken: string,
  onImageReceived?: (image: GeneratedImage) => void
): Promise<GenerateImageResponse> => {
  try {
    const endpoint = `${BASE_URL}/generate-image`;
    
    // Prepare request body matching new API format
    const requestBody: Record<string, unknown> = {
      prompt: request.prompt,
      customer_id: request.customer_id,
      provider: request.provider,
      num_images: request.num_images,
      aspect_ratio: request.aspect_ratio,
    };
    if (request.provider === "openai" && request.quality) {
      requestBody.quality = request.quality;
    }
    
    const response = await axios.post(
      endpoint, 
      requestBody, 
      {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        // Enable streaming responses if backend supports it
        responseType: 'json',
        onDownloadProgress: (progressEvent) => {
          // If backend sends images incrementally, handle them here
          if (progressEvent.event.target?.response) {
            try {
              const partialData = JSON.parse(progressEvent.event.target.response);
              if (partialData.image && onImageReceived) {
                onImageReceived(partialData.image);
              }
            } catch (e) {
              // Not valid JSON yet, keep waiting
            }
          }
        }
      }
    );

    // Handle new response format: response.data.images array
    const images = response.data.images || [];
    
    // Emit each image to callback for progressive rendering
    if (onImageReceived && images.length > 0) {
      images.forEach((image: GeneratedImage) => {
        onImageReceived(image);
      });
    }

    return {
      success: true,
      images,
    };
  } catch (error: any) {
    debug.error('Error generating images:', error);
    const data = error.response?.data;
    const backendMsg =
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.error === 'string' && data.error) ||
      null;

    return {
      success: false,
      images: [],
      error: backendMsg || error.message || 'Failed to generate images',
    };
  }
};

/**
 * Saves chat-generated posts to Content API
 * Handles customer fetch and platform mapping internally
 * @param customerId - Customer ID
 * @param posts - Array of post data from chat
 * @param idToken - Auth token
 * @returns Promise with post_ids and success message
 */
export const saveChatPosts = async (
  customerId: string,
  posts: Array<{
    platform: string;
    title: string;
    content: string;
    image_url: string;
    video_url?: string;
    hashtags: string[];
    call_to_action: string;
    scheduled_time?: string;
    post_type?: string;
    carousel_media?: PostCarouselMediaItem[];
    carousel_slides?: Array<{ url: string; type?: string; caption?: string }>;
    linkedinTarget?: "personal" | "business";
  }>,
  idToken: string
): Promise<{ post_ids: string[]; message: string }> => {
  try {
    // 1. Fetch customer data with connected_profiles
    const customer = await fetchCustomerById(customerId, idToken);

    // 2. Map platforms to business_profile_ids using existing utility.
    //    Pass per-post hints so LinkedIn resolves to the chosen profile (personal vs Business Page).
    const platformMappings = mapAllowedPlatforms(
      posts.map((p) => p.platform),
      customer?.connected_profiles || [],
      posts.map((p) => ({ linkedinTarget: p.linkedinTarget }))
    );

    debug.log('📋 Saving posts from chat (via contentService):', {
      postCount: posts.length,
      platforms: posts.map((p) => p.platform),
      platformMappings,
    });

    // 3. Call Content API to save posts
    const result = await createPostsFromChat(
      customerId,
      posts,
      platformMappings,
      idToken
    );

    // 4. Patch carousel into Dynamo when batch-create drops post_type / carousel_media
    if (result.post_ids && Array.isArray(result.post_ids)) {
      const patchPromises = result.post_ids.map(async (postId, index) => {
        const p = posts[index];
        if (!postId) return;

        const updatePayload: any = {};
        if (
          p.post_type === "CAROUSEL" &&
          Array.isArray(p.carousel_media) &&
          p.carousel_media.length > 0
        ) {
          updatePayload.post_type = p.post_type;
          updatePayload.carousel_media = p.carousel_media;
        }

        if (Array.isArray(p.carousel_slides) && p.carousel_slides.length > 0) {
          updatePayload.carousel_slides = p.carousel_slides;
        }

        if (Object.keys(updatePayload).length > 0) {
          try {
            await updatePostFields(
              postId,
              updatePayload,
              idToken,
              customerId
            );
            debug.log(`Patched carousel/slides data for post ${postId}`);
          } catch (err) {
            debug.error(`Failed to patch carousel/slides data for post ${postId}`, err);
          }
        }
      });
      await Promise.allSettled(patchPromises);
    }

    debug.log('✅ Posts saved successfully:', result);
    return result;
  } catch (error: any) {
    debug.error('❌ Failed to save chat posts:', error);
    throw new Error(
      error.response?.data?.message || 
      error.message || 
      'Failed to save posts. Please try again.'
    );
  }
};

// ============================================
// UGC Video API Functions
// ============================================

const mapAvatarResponse = (avatar: any, avatarType: 'avatar' | 'talking_photo' = 'avatar'): Avatar => ({
  avatar_id: avatar.avatar_id ?? avatar.id,
  name: avatar.name ?? avatar.avatar_id ?? avatar.id,
  thumbnail_url: avatar.thumbnail_url ?? '',
  provider: avatar.provider ?? 'HEYGEN',
  avatar_type: (avatar.avatar_type as 'avatar' | 'talking_photo') ?? avatarType,
  ...(avatar.preview_video_url ? { preview_video_url: avatar.preview_video_url } : {}),
  ...(avatar.gender ? { gender: avatar.gender } : {}),
});

/**
 * Fetch all avatars for UGC video generation.
 */
export const fetchUGCVideoAvatars = async (
  customerId: string,
  idToken: string,
): Promise<{
  avatars: Avatar[];
  customAvatars: Avatar[];
}> => {
  const endpoint =
    `${BASE_URL}/videos/avatars?provider=HEYGEN` +
    `&customer_id=${encodeURIComponent(customerId)}`;

  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const rawAvatars = response.data.avatars ?? response.data ?? [];
    const rawCustom = response.data.custom_avatars ?? [];
    return {
      avatars: rawAvatars.map((a: any) => mapAvatarResponse(a, 'avatar')),
      customAvatars: rawCustom.map((a: any) => mapAvatarResponse(a, 'talking_photo')),
    };
  } catch (error: any) {
    debug.error('Error fetching UGC video avatars:', error);
    throw new Error(
      error.response?.data?.message ?? error.message ?? 'Failed to fetch avatars'
    );
  }
};

/**
 * Fetch available voices for UGC video generation
 * @param customerId - Customer ID
 * @param idToken - Cognito ID token
 * @returns Promise with array of voice objects
 */
export const fetchUGCVideoVoices = async (
  customerId: string,
  idToken: string
): Promise<Voice[]> => {
  // customer_id makes the backend include this customer's custom (cloned) voices.
  const endpoint = `${BASE_URL}/videos/voices?provider=HEYGEN&customer_id=${encodeURIComponent(customerId)}`;

  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const voices = response.data.voices ?? response.data ?? [];
    return voices.map((voice: any) => ({
      voice_id: voice.voice_id ?? voice.id,
      name: voice.name ?? voice.voice_id ?? voice.id,
      language: voice.language ?? 'Unknown',
      gender: voice.gender ?? 'Unknown',
      sample_url: voice.sample_url ?? voice.preview_audio ?? voice.sampleUrl ?? voice.preview_url ?? voice.preview,
      provider: voice.provider,
      is_custom: voice.is_custom === true,
    }));
  } catch (error: any) {
    debug.error('Error fetching UGC video voices:', error);
    throw new Error(
      error.response?.data?.message ?? error.message ?? 'Failed to fetch voices'
    );
  }
};

/**
 * Clone the customer's voice from an audio sample (ElevenLabs Instant Voice Clone).
 * The created voice then appears in fetchUGCVideoVoices with is_custom: true.
 */
export const uploadCustomVoice = async (
  customerId: string,
  name: string,
  audioBase64: string,
  contentType: string,
  idToken: string
): Promise<{ voice_id: string; name: string; sample_url?: string }> => {
  const endpoint = `${BASE_URL}/videos/voices/upload`;

  try {
    const response = await axios.post(
      endpoint,
      { customer_id: customerId, name, audio_base64: audioBase64, content_type: contentType },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const backendMsg = error.response?.data?.message ?? error.response?.data?.error;
    debug.error('Error uploading custom voice:', error);
    throw new Error(backendMsg ?? error.message ?? 'Failed to upload custom voice');
  }
};

/**
 * Generate UGC style video
 * @param params - Video generation parameters
 * @param idToken - Cognito ID token
 * @returns Promise with video generation response
 */
/** Single path for UGC video generate. REACT_APP_CONTENT_API_URL must be the API base (e.g. .../Prod/content). */
const UGC_VIDEO_GENERATE_PATH = '/videos/v2/generate';

export const generateUGCVideo = async (
  params: GenerateUGCVideoParams,
  idToken: string,
  idempotencyKey?: string
): Promise<VideoGenerationResponse> => {
  const endpoint = `${BASE_URL}${UGC_VIDEO_GENERATE_PATH}`;

  try {
    const response = await axios.post(endpoint, params, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
        ...(idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {}),
      },
      timeout: 30000, // 30 second timeout to prevent hanging
    });

    return response.data;
  } catch (error: any) {
    const status = error.response?.status;
    const urlMsg = `Request URL: ${endpoint}`;
    const backendMsg = error.response?.data?.message ?? error.response?.data?.error;
    const hint =
      status === 404
        ? `Endpoint not found (404). Check REACT_APP_CONTENT_API_URL matches the API base. ${urlMsg}`
        : error.code === 'ERR_NETWORK' || !error.response
          ? `Network error. If CORS or connection failed, verify the API URL. ${urlMsg}`
          : backendMsg ?? error.message;
    debug.error('Error generating UGC video:', error);
    throw new Error(hint || 'Failed to generate video');
  }
};

/** Path template for UGC video status. Base from REACT_APP_CONTENT_API_URL. */
const getUGCVideoStatusPath = (videoId: string) => `/videos/${videoId}/status`;

export const pollUGCVideoStatus = async (
  videoId: string,
  idToken: string
): Promise<VideoStatusResponse> => {
  const endpoint = `${BASE_URL}${getUGCVideoStatusPath(videoId)}`;

  try {
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 second timeout for polling requests
    });

    return response.data;
  } catch (error: any) {
    const status = error.response?.status;
    const urlMsg = `Request URL: ${endpoint}`;
    const backendMsg = error.response?.data?.message ?? error.response?.data?.error;
    const hint =
      status === 404
        ? `Video or endpoint not found (404). ${urlMsg}`
        : error.code === 'ERR_NETWORK' || !error.response
          ? `Network error. ${urlMsg}`
          : backendMsg ?? error.message;
    debug.error('Error polling UGC video status:', error);
    throw new Error(hint || 'Failed to get video status');
  }
};

export interface AnalyzeCaptionsResponse {
  cues: CaptionCue[];
  caption_cues_url: string;
}

export const analyzeUGCVideoCaptions = async (
  videoId: string,
  customerId: string,
  idToken: string,
  // SGL-143: a user-uploaded base video has no VideoGenerationStatus row and no script —
  // pass its S3/CloudFront URL so the backend can synthesise a record and still run STT.
  avatarVideoUrl?: string
): Promise<AnalyzeCaptionsResponse> => {
  const endpoint = `${BASE_URL}/videos/${videoId}/captions/analyze`;
  const response = await axios.post(
    endpoint,
    {
      customer_id: customerId,
      ...(avatarVideoUrl ? { avatar_video_url: avatarVideoUrl } : {}),
    },
    {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      timeout: 90000,
    }
  );
  return response.data;
};

/** One item in background_media for Creatomate assembly. */
export interface UGCVideoAssemblyBackgroundItem {
  media_id: string;
  time: number;
  duration: number;
  fit?: 'contain' | 'cover';
  zoom?: number;
  fade_in?: boolean;
  fade_out?: boolean;
  /** Volume percent for video segments (0–100). 0 = muted (default for stock video). */
  volume?: number;
}

/** Request body for UGC video assembly (Creatomate). */
export interface UGCVideoAssemblyRequestBody {
  background_media: UGCVideoAssemblyBackgroundItem[];
  background_music_url?: string;
  /** 0–100 mix level for background music in the final render (Creatomate). */
  background_music_volume?: number;
  captions_enabled?: boolean;
  captions_text?: string;
  captions_srt_url?: string;
  caption_design?: string;
  caption_spacing?: string;
  /** Text overlays to burn into the video at specific timestamps. */
  text_overlays?: Array<{
    text: string;
    time: number;
    duration: number;
    style?: string;
    position?: string;
  }>;
}

/**
 * Submit custom image segments for an existing UGC video (assembly / Creatomate).
 * @param videoId - Video ID from initial generate response
 * @param body - Object with background_media array of { media_id, time, duration }
 * @param idToken - Cognito ID token
 */
export const postUGCVideoAssembly = async (
  videoId: string,
  body: UGCVideoAssemblyRequestBody,
  idToken: string
): Promise<void> => {
  const endpoint = `${BASE_URL}/videos/${videoId}/assembly`;

  try {
    await axios.post(endpoint, body, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (error: any) {
    const status = error.response?.status;
    const urlMsg = `Request URL: ${endpoint}`;
    const backendMsg = error.response?.data?.message ?? error.response?.data?.error;
    const hint =
      status === 404
        ? `Video or assembly endpoint not found (404). ${urlMsg}`
        : error.code === 'ERR_NETWORK' || !error.response
          ? `Network error. ${urlMsg}`
          : backendMsg ?? error.message;
    debug.error('Error calling UGC video assembly:', error);
    throw new Error(hint || 'Failed to apply images to video');
  }
};

/**
 * Upload a custom avatar image (base64) for the customer.
 * POST /videos/avatars/upload with { customer_id, image_base64, content_type }.
 * content_type must be "image/jpeg" or "image/png".
 */
export const uploadCustomAvatar = async (
  customerId: string,
  imageBase64: string,
  idToken: string,
  contentType: string
): Promise<unknown> => {
  const endpoint = `${BASE_URL}/videos/avatars/upload`;

  try {
    const response = await axios.post(
      endpoint,
      { customer_id: customerId, image_base64: imageBase64, content_type: contentType },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const urlMsg = `Request URL: ${endpoint}`;
    const backendMsg = error.response?.data?.message ?? error.response?.data?.error;
    const hint =
      error.code === 'ERR_NETWORK' || !error.response
        ? `Network error. ${urlMsg}`
        : backendMsg ?? error.message;
    debug.error('Error uploading custom avatar:', error);
    throw new Error(hint || 'Failed to upload custom avatar');
  }
};

/**
 * Saves chat-generated videos to Content API
 * Handles customer fetch and platform mapping internally
 * @param customerId - Customer ID
 * @param videos - Array of video data from chat
 * @param idToken - Auth token
 * @returns Promise with post_ids and success message
 */
export const saveChatVideos = async (
  customerId: string,
  videos: Array<{
    platform: string;
    title: string;
    content: string;
    video_url: string;
    hashtags?: string[];
    call_to_action?: string;
    /** LinkedIn/LinkedIn Reels only: which connected profile to target. */
    linkedinTarget?: "personal" | "business";
  }>,
  idToken: string
): Promise<{ post_ids: string[]; message: string }> => {
  try {
    // 1. Fetch customer data with connected_profiles
    const customer = await fetchCustomerById(customerId, idToken);

    // 2. Map platforms to business_profile_ids using existing utility
    const platformMappings = mapAllowedPlatforms(
      videos.map((v) => v.platform),
      customer?.connected_profiles || [],
      videos.map((v) => (v.linkedinTarget ? { linkedinTarget: v.linkedinTarget } : undefined))
    );

    // 3. Transform videos to post format (posts can have video_url)
    const postsToSave = videos.map((video) => ({
      platform: video.platform,
      title: video.title || "Video",
      content: video.content || "",
      image_url: "", // No image for video-only posts
      video_url: video.video_url || "", // Ensure string; backend expects video_url for video posts
      hashtags: Array.isArray(video.hashtags) ? video.hashtags : [],
      call_to_action: video.call_to_action || "",
    }));

    debug.log('📋 Saving videos from chat (via contentService):', {
      videoCount: videos.length,
      platforms: videos.map((v) => v.platform),
      videoUrls: postsToSave.map((p) => p.video_url),
      platformMappings,
    });

    // 4. Call Content API to save posts (with video_url)
    const result = await createPostsFromChat(
      customerId,
      postsToSave,
      platformMappings,
      idToken
    );

    debug.log('✅ Videos saved successfully:', result);
    return result;
  } catch (error: any) {
    debug.error('❌ Failed to save chat videos:', error);
    throw new Error(
      error.response?.data?.message ||
      error.message ||
      'Failed to save videos. Please try again.'
    );
  }
};

/**
 * Fetch all generated videos for a customer
 * @param customerId - Customer ID
 * @param idToken - Cognito ID token
 * @returns Promise with array of video history items
 */
export const fetchVideosByCustomer = async (
  customerId: string,
  idToken: string
): Promise<any[]> => {
  const endpoint = `${BASE_URL}/videos/list?customer_id=${customerId}`;

  try {
    const response = await axios.get(endpoint, {
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const videos = response.data.videos || response.data || [];
    return videos.sort((a: any, b: any) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  } catch (error: any) {
    debug.error('Error fetching videos by customer:', error);
    throw new Error(
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch videos'
    );
  }
};

// ─── Pexels video search ────────────────────────────────────────────────────

export interface PexelsVideoResult {
  id: string;
  duration: number;
  thumbnail_url: string;
  video_url: string;
  width: number;
  height: number;
  photographer: string;
  pexels_url: string;
}

export interface PexelsVideoSearchResponse {
  videos: PexelsVideoResult[];
  total_results: number;
  page: number;
  per_page: number;
  has_next: boolean;
}

/**
 * Search Pexels for stock videos.
 *
 * Uses REACT_APP_PEXELS_API_KEY from .env.local when set (direct browser call).
 * Falls back to the backend proxy endpoint when the env var is absent so the
 * Pexels key stays server-side in production (stored in AWS Secrets Manager).
 */
export const searchPexelsVideos = async (
  query: string,
  idToken: string,
  options: { perPage?: number; page?: number; orientation?: 'portrait' | 'landscape' | 'square' } = {}
): Promise<PexelsVideoSearchResponse> => {
  const { perPage = 15, page = 1, orientation = 'portrait' } = options;

  const pexelsKey = process.env.REACT_APP_PEXELS_API_KEY;

  if (pexelsKey) {
    // Direct call — key comes from .env.local (dev only)
    const params = new URLSearchParams({
      query,
      per_page: String(perPage),
      page: String(page),
      orientation,
    });
    try {
      const response = await axios.get(
        `https://api.pexels.com/videos/search?${params}`,
        { headers: { Authorization: pexelsKey } }
      );
      const data = response.data;
      const videos: PexelsVideoResult[] = (data.videos ?? []).flatMap((v: any) => {
        const mp4Files = (v.video_files ?? []).filter(
          (f: any) => (f.file_type ?? '').toLowerCase() === 'video/mp4'
        );
        const hd = mp4Files.filter((f: any) => ['hd', 'sd'].includes(f.quality ?? ''));
        const candidates = hd.length ? hd : mp4Files;
        const best = candidates.sort((a: any, b: any) => (b.width ?? 0) - (a.width ?? 0))[0];
        if (!best) return [];
        return [{
          id: String(v.id ?? ''),
          duration: v.duration ?? 0,
          thumbnail_url: v.image ?? '',
          video_url: best.link ?? '',
          width: v.width ?? 0,
          height: v.height ?? 0,
          photographer: v.user?.name ?? 'Pexels',
          pexels_url: v.url ?? '',
        }];
      });
      return {
        videos,
        total_results: data.total_results ?? 0,
        page: data.page ?? page,
        per_page: perPage,
        has_next: videos.length >= perPage,
      };
    } catch (error: any) {
      debug.error('Pexels direct search error:', error);
      throw new Error(error.message || 'Failed to search Pexels videos');
    }
  }

  // Production path — backend proxy keeps the key server-side
  const params = new URLSearchParams({
    query,
    per_page: String(perPage),
    page: String(page),
    orientation,
  });
  const endpoint = `${BASE_URL}/pexels/videos?${params}`;
  try {
    const response = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return response.data as PexelsVideoSearchResponse;
  } catch (error: any) {
    debug.error('Error searching Pexels videos (backend):', error);
    throw new Error(
      error.response?.data?.error || error.message || 'Failed to search Pexels videos'
    );
  }
};

/**
 * Register an external media item (e.g. a Pexels video URL) in the customer media library.
 * Returns the newly created media_id so the item can be added to the timeline.
 */
export const registerExternalMedia = async (
  params: {
    customerId: string;
    mediaType: 'VIDEO' | 'IMAGE';
    externalUrl: string;
    s3Key: string;
    tags?: string[];
    metadata?: Record<string, unknown>;
  },
  idToken: string
): Promise<{ media_id: string; cloudfront_url: string }> => {
  const endpoint = `${BASE_URL}/media`;

  const body = {
    customer_id: params.customerId,
    media_type: params.mediaType,
    s3_key: params.s3Key,
    cloudfront_url: params.externalUrl,
    tags: params.tags ?? [],
    metadata: params.metadata ?? {},
    processed: true,
  };

  try {
    const response = await axios.post(endpoint, body, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });
    return {
      media_id: response.data.media_id,
      cloudfront_url: response.data.cloudfront_url,
    };
  } catch (error: any) {
    debug.error('Error registering external media:', error);
    throw new Error(
      error.response?.data?.error || error.message || 'Failed to register media'
    );
  }
};

// ─── Video Draft CRUD ────────────────────────────────────────────────────────

export interface VideoDraftRecord {
  draft_id: string;
  customer_id: string;
  title: string;
  draft_data: UGCVideoDraft;
  created_at: string;
  updated_at: string;
}

export const listVideoDrafts = async (
  customerId: string,
  idToken: string
): Promise<VideoDraftRecord[]> => {
  const res = await axios.get(`${BASE_URL}/videos/drafts?customer_id=${customerId}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return (res.data.drafts ?? []) as VideoDraftRecord[];
};

export const createVideoDraft = async (
  customerId: string,
  draftId: string,
  title: string,
  draftData: Omit<UGCVideoDraft, 'draft_id' | 'title' | 'saved_at'>,
  idToken: string
): Promise<{ draft_id: string }> => {
  const res = await axios.post(
    `${BASE_URL}/videos/drafts`,
    { customer_id: customerId, draft_id: draftId, title, draft_data: draftData },
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return res.data;
};

export const updateVideoDraft = async (
  customerId: string,
  draftId: string,
  updates: { title?: string; draft_data?: Partial<UGCVideoDraft> },
  idToken: string
): Promise<void> => {
  await axios.put(
    `${BASE_URL}/videos/drafts/${draftId}`,
    { customer_id: customerId, ...updates },
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
};

export const deleteVideoDraft = async (
  customerId: string,
  draftId: string,
  idToken: string
): Promise<void> => {
  await axios.delete(
    `${BASE_URL}/videos/drafts/${draftId}?customer_id=${customerId}`,
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
};
// --- SGL-344: put the brand's mark on a generated image ----------------------

/** Where a mark may sit. Mirrors lambdas/image_generator/logo_placement.py. */
export const LOGO_POSITIONS = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
] as const;

export type LogoPosition = (typeof LOGO_POSITIONS)[number];

/** `light` is the mark FOR light backgrounds, not a light-coloured mark. */
export type LogoVariant = "light" | "dark";

export interface ApplyBrandLogoParams {
  customerId: string;
  imageUrl: string;
  position?: LogoPosition;
  /** Omit to let the backend measure and suggest; set to override it. */
  variant?: LogoVariant;
  /** Mark width as a fraction of image width. Backend clamps to 0.05–0.40. */
  scale?: number;
}

export interface ApplyBrandLogoResponse {
  image_url: string;
  s3_key: string;
  media_id: string;
  logo_variant: LogoVariant;
  /** False when the customer overrode, or when only one mark exists to use. */
  auto_selected: boolean;
  /** What the measurement would have picked — present even on an override. */
  suggested_variant: LogoVariant;
  /** The colour measured under the mark's own box, for explaining the choice. */
  background_hex: string;
  contrast_ratio: number;
  position: LogoPosition;
  available_variants: LogoVariant[];
}

const BRAND_LOGO_PATH = "/brand-logo";

export const applyBrandLogo = async (
  { customerId, imageUrl, position, variant, scale }: ApplyBrandLogoParams,
  idToken: string
): Promise<ApplyBrandLogoResponse> => {
  const endpoint = `${BASE_URL}${BRAND_LOGO_PATH}`;

  try {
    const response = await axios.post(
      endpoint,
      {
        customer_id: customerId,
        image_url: imageUrl,
        ...(position ? { position } : {}),
        // "auto" is the backend's own default; sending it explicitly keeps the
        // override and the suggestion as two visibly different requests.
        variant: variant ?? "auto",
        ...(scale !== undefined ? { scale } : {}),
      },
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        timeout: 60000,
      }
    );
    return response.data;
  } catch (error: any) {
    // The backend distinguishes "you have no logo" (409) from a real failure on
    // purpose — surfacing its message verbatim is what makes that worth doing.
    const backendMsg =
      error.response?.data?.message ?? error.response?.data?.error;
    debug.error("Error applying brand logo:", error);
    throw new Error(backendMsg || "Failed to place the brand logo.");
  }
};
